import type { DialogServiceMethods } from 'primevue/dialogservice'

interface QueuedRequest {
  request: string
  options: Record<string, unknown>
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
}

// Singleton state (shared across all usages)
let isModalOpen = false
let refreshInFlight: Promise<boolean> | null = null
const failedRequests: QueuedRequest[] = []

export function useSessionRestoration() {
  const log = useLogger('composable:session-restoration')

  /**
   * Queue a failed request for retry after re-authentication
   */
  function queueRequest(request: QueuedRequest): void {
    failedRequests.push(request)
    log.info(`📋 Queued request: ${request.request} (total: ${failedRequests.length})`)
  }

  /**
   * Attempt silent token refresh using existing refresh token
   * Returns true if successful, false if refresh failed
   */
  async function attemptSilentRefresh(): Promise<boolean> {
    // Los 401 del arranque llegan casi juntos. Sin compartir la promesa, el primero
    // refresca y todos los demás reciben false y abren el modal — que era justo lo
    // que se veía en producción.
    if (refreshInFlight) {
      log.info('🔄 Silent refresh already in progress, esperando el resultado')
      return refreshInFlight
    }

    refreshInFlight = (async () => {
      log.info('🔄 Attempting silent token refresh...')
      try {
        const refreshed = await useRefreshToken()
        if (refreshed) {
          log.info('✅ Silent token refresh successful')
        }
        else {
          log.warn('⚠️ Silent refresh failed — el token no se renovó')
        }
        return refreshed
      }
      catch (error) {
        log.error('❌ Silent token refresh failed:', error)
        return false
      }
      finally {
        refreshInFlight = null
      }
    })()

    return refreshInFlight
  }

  /**
   * Show the session restore modal
   * Uses DialogService to open the modal without losing page state
   */
  async function showModal(): Promise<{ success: boolean, cancelled?: boolean }> {
    if (isModalOpen) {
      log.info('🚫 Modal already open, skipping')
      return { success: false }
    }

    log.info('🔐 Attempting to open session restore modal')

    try {
      const nuxtApp = useNuxtApp()
      const $dialog = nuxtApp.$dialog as DialogServiceMethods

      if (!$dialog) {
        log.error('❌ DialogService not available')
        return { success: false }
      }

      isModalOpen = true
      log.info('🔐 Opening session restore modal via DialogService')

      // Dynamically import the modal component
      const SessionRestoreModal = defineAsyncComponent(
        () => import('~/components/Auth/SessionRestoreModal.vue'),
      )

      return new Promise((resolve) => {
        $dialog.open(SessionRestoreModal, {
          props: {
            header: '',
            modal: true,
            closable: false,
            dismissableMask: false,
            position: 'center',
            style: { width: '400px' },
            pt: {
              root: { class: 'session-restore-dialog' },
              header: { class: 'hidden' },
            },
          },
          onClose: (options) => {
            isModalOpen = false
            const result = options?.data as { success: boolean, cancelled?: boolean } | undefined

            if (result?.success) {
              log.info('✅ Session restored successfully')
              resolve({ success: true })
            }
            else if (result?.cancelled) {
              log.info('🚪 User cancelled session restore')
              resolve({ success: false, cancelled: true })
            }
            else {
              log.info('❌ Modal closed without success')
              resolve({ success: false })
            }
          },
        })
      })
    }
    catch (error) {
      log.error('❌ Failed to open session restore modal:', error)
      isModalOpen = false
      return { success: false }
    }
  }

  /**
   * Retry all queued requests after successful re-authentication
   * Returns the original $fetch instance for proper retries
   */
  async function retryFailedRequests(): Promise<void> {
    log.info(`🔄 Retrying ${failedRequests.length} queued requests`)

    while (failedRequests.length > 0) {
      const request = failedRequests.shift()
      if (!request) continue

      try {
        const sbAccessToken = useCookie('sb-access-token')
        const updatedOptions = {
          ...request.options,
          headers: {
            ...(request.options.headers as Record<string, string> || {}),
            Authorization: `Bearer ${sbAccessToken.value}`,
          },
        }

        log.info(`🔄 Retrying: ${request.request}`)
        const result = await $fetch(request.request, updatedOptions)
        request.resolve(result)
      }
      catch (error) {
        log.error(`❌ Retry failed for: ${request.request}`, error)
        request.reject(error)
      }
    }

    log.info('✅ All queued requests processed')
  }

  /**
   * Reject all queued requests (used when user cancels re-auth)
   */
  function rejectAllRequests(): void {
    log.info(`❌ Rejecting ${failedRequests.length} queued requests`)

    while (failedRequests.length > 0) {
      const request = failedRequests.shift()
      if (request) {
        request.reject(new Error('Session restoration cancelled'))
      }
    }
  }

  /**
   * Check if the modal is currently open
   */
  function isOpen(): boolean {
    return isModalOpen
  }

  /**
   * Get the number of queued requests
   */
  function queueSize(): number {
    return failedRequests.length
  }

  return {
    queueRequest,
    attemptSilentRefresh,
    showModal,
    retryFailedRequests,
    rejectAllRequests,
    isOpen,
    queueSize,
  }
}
